import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { AgentApiItem } from "@/types/api";
import api from "@/services/api";

export const AGENT_STATUS_TO_STATUS: Record<string, string> = {
  PTP: "open",
  NC: "in_progress",
  NITP: "escalated",
  CB: "in_progress",
  RVT: "in_progress",
  SHIFTED: "escalated",
  VT: "in_progress",
  FPC: "closed",
};

export function statusFromAgentStatus(
  agentStatus: string | undefined | null,
): string | null {
  if (!agentStatus) return null;
  return AGENT_STATUS_TO_STATUS[agentStatus] ?? null;
}

export interface AgentFilter {
  level?: string;
  region_code?: string;
  status?: string;
  searchTerm?: string;
}

export interface AgentSort {
  field: keyof AgentApiItem | null;
  direction: "asc" | "desc";
}

interface AgentsState {
  items: AgentApiItem[];
  loading: boolean;
  error: string | null;
  filter: AgentFilter;
  sort: AgentSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: AgentsState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

const normalizeAgentApiItem = (payload: any): AgentApiItem => ({
  id: payload.id,
  user_id: payload.user_id,
  name: payload.user?.name ?? payload.name,
  email: payload.user?.email ?? payload.email,
  phone: payload.user?.name ?? payload.phone,
  status: payload.status,
  designation: payload.designation ?? null,
  level: payload.level ?? "level_1",
  employee_code: payload.employee_code ?? null,
  reports_to_user_id: payload.reports_to_user_id ?? null,
  supervisor_name: payload.supervisor_name ?? null,
  // @ts-ignore – regions is not declared in AgentApiItem but required here
  regions: Array.isArray(payload.regions)
    ? payload.regions.map((region: any) => ({
        id: region.id,
        name: region.name,
        code: region.code,
      }))
    : [],
  branches: Array.isArray(payload.branches)
    ? payload.branches.map((branch: any) => ({
        id: branch.id,
        name: branch.name,
        code: branch.code,
      }))
    : [],
  active_cases: payload.active_cases ?? 0,
  collections: payload.collections ?? 0,
});

export const fetchAgents = createAsyncThunk(
  "agents/fetchAgents",
  async (
    params: {
      page?: number;
      per_page?: number;
      searchTerm?: string;
      month?: number;
      year?: number;
    } = {},
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as { agents: AgentsState };
      const { currentPage, pageSize, filter } = state.agents;
      const queryParams = new URLSearchParams({
        page: String(params?.page ?? currentPage),
        per_page: String(params?.per_page ?? pageSize),
      });

      const searchTerm = params?.searchTerm ?? filter.searchTerm;
      if (searchTerm) {
        queryParams.append("search", searchTerm);
      }
      if (filter.level) {
        queryParams.append("level", filter.level);
      }
      if (filter.region_code) {
        queryParams.append("region_code", filter.region_code);
      }
      if (filter.status) {
        queryParams.append("status", filter.status);
      }
      if (params.month) {
        queryParams.append("month", String(params.month));
      }
      if (params.year) {
        queryParams.append("year", String(params.year));
      }

      const response = await api.get<{
        data: AgentApiItem[];
        meta: {
          current_page: number;
          per_page: number;
          total: number;
          last_page: number;
          from: number | null;
          to: number | null;
        };
      }>(`/v1/agents?${queryParams}`);

      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch agents",
      );
    }
  },
);

export const createAgent = createAsyncThunk(
  "agents/createAgent",
  async (
    data: {
      name: string;
      email: string;
      phone: string;
      password: string;
      employee_code?: string | null;
      designation?: string | null;
      level?: "level_1" | "level_2" | "level_3";
      reports_to_user_id?: number | null;
      status?: "online" | "offline" | "busy" | "inactive";
      joined_on?: string | null;
      region_ids?: number[];
      branch_ids?: number[];
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post("/v1/agents", data);
      return normalizeAgentApiItem(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create agent",
      );
    }
  },
);

export const updateAgent = createAsyncThunk(
  "agents/updateAgent",
  async ({ id, ...data }: any) => {
    const res = await api.patch(`/v1/agents/${id}`, data);
    return normalizeAgentApiItem(res.data);
  },
);

export const bulkImportAgents = createAsyncThunk(
  "agents/bulkImportAgents",
  async (
    data: {
      rows: Record<string, unknown>[];
      options?: { region_ids?: number[] };
    },
    { rejectWithValue },
  ) => {
    try {
      const payload = {
        rows: data.rows,
        ...(data.options?.region_ids?.length
          ? { options: { region_ids: data.options.region_ids } }
          : {}),
      };
      const response = await api.post("/v1/agents/import", payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to import agents",
      );
    }
  },
);

const agentsSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    setAgentFilter: (state, action: PayloadAction<AgentFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setAgentSort: (state, action: PayloadAction<AgentSort>) => {
      state.sort = action.payload;
      state.currentPage = 1;
    },
    setAgentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setAgentPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetAgentFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addAgent: (state, action: PayloadAction<AgentApiItem>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updateAgent: (state, action: PayloadAction<AgentApiItem>) => {
      const index = state.items.findIndex((a) => a.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteAgent: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((a) => a.id !== Number(action.payload));
      state.total -= 1;
    },
    resetAgents: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAgents.fulfilled, (state, action) => {
        state.loading = false;
        const rawItems = Array.isArray(action.payload.data)
          ? action.payload.data
          : [];
        state.items = rawItems.map(normalizeAgentApiItem);
        state.total = action.payload.meta?.total ?? 0;
        state.currentPage = action.payload.meta?.current_page ?? 1;
        state.pageSize = action.payload.meta?.per_page ?? state.pageSize;
        state.error = null;
      })
      .addCase(fetchAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      .addCase(updateAgent.fulfilled, (state, action) => {
        const index = state.items.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateAgent.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(bulkImportAgents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkImportAgents.fulfilled, (state) => {
        state.loading = false;
        // Refresh agents list after import
        // The component will call fetchAgents separately
      })
      .addCase(bulkImportAgents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setAgentFilter,
  setAgentSort,
  setAgentPage,
  setAgentPageSize,
  resetAgentFilters,
  addAgent,
  deleteAgent,
  resetAgents,
} = agentsSlice.actions;
export default agentsSlice.reducer;

// Selectors
export const selectAgents = (state: any) => state.agents.items;
export const selectAgentsLoading = (state: any) => state.agents.loading;
export const selectAgentsError = (state: any) => state.agents.error;
export const selectAgentFilter = (state: any) => state.agents.filter;
export const selectAgentSort = (state: any) => state.agents.sort;
export const selectAgentPagination = (state: any) => ({
  currentPage: state.agents.currentPage,
  pageSize: state.agents.pageSize,
  total: state.agents.total,
});

export const selectFilteredAgents = createSelector(
  [
    (state: any) => state.agents.items,
    (state: any) => state.agents.filter,
    (state: any) => state.agents.sort,
    (state: any) => state.agents.currentPage,
    (state: any) => state.agents.pageSize,
    (state: any) => state.agents.total,
  ],
  (items, filter, sort, currentPage, pageSize, total) => {
    let filtered = Array.isArray(items) ? [...items] : [];

    if (filter.level) {
      filtered = filtered.filter((a) => a.level === filter.level);
    }
    if (filter.region_code) {
      filtered = filtered.filter((a) =>
        a.regions?.some((r) => r.code === filter.region_code),
      );
    }
    if (filter.status) {
      filtered = filtered.filter((a) => a.status === filter.status);
    }
    if (filter.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.name?.toLowerCase().includes(term) ||
          a.email?.toLowerCase().includes(term),
      );
    }

    if (sort.field) {
      filtered.sort((a, b) => {
        const aVal = a[sort.field!] || "";
        const bVal = b[sort.field!] || "";
        const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        return sort.direction === "asc" ? comparison : -comparison;
      });
    }

    return {
      data: filtered,
      total,
      currentPage,
      pageSize,
      hasNextPage: currentPage < Math.ceil(total / pageSize),
      hasPrevPage: currentPage > 1,
    };
  },
);
